"use client";

import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { usePathname, useRouter } from "next/navigation";
import { Bell, X } from "lucide-react";
import NotificationItem from "@/app/components/notifications/NotificationItem";
import { useNotifications } from "@/app/hooks/useNotifications";
import useInitializeUserFromCookie from "@/app/components/hooks/useInitializeUserFromCookie";
import { AppConstants } from "@/app/lib/util/AppConstants";
import { NotificationType } from "@/app/lib/models/NotificationModel";
import {
  extractMemberIdFromNotificationData,
  startTraineeChatWithMember,
} from "@/app/lib/util/traineeMemberChat.util";
import { canDiscardNotification } from "@/app/lib/util/notification-priority.util";
import "@/app/styles/chat/_notification-drawer.scss";

interface NotificationDrawerProps {
  open: boolean;
  onClose: () => void;
  hook: ReturnType<typeof useNotifications>;
}

export default function NotificationDrawer({
  open,
  onClose,
  hook,
}: NotificationDrawerProps) {
  const router = useRouter();
  const currentUrl = usePathname() as string;
  const userDetailsFromCookie = useInitializeUserFromCookie(currentUrl);
  const [mounted, setMounted] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const dismissibleCount = useMemo(
    () =>
      hook.notifications.filter((item) => canDiscardNotification(item)).length,
    [hook.notifications],
  );

  const handleClearAll = async () => {
    if (isClearing || dismissibleCount === 0) return;
    setIsClearing(true);
    try {
      await hook.clearAll();
    } finally {
      setIsClearing(false);
    }
  };

  const handleItemClick = async (
    item: ReturnType<typeof useNotifications>["notifications"][number],
  ) => {
    const isTrainee =
      userDetailsFromCookie?.role === AppConstants.Roles.TRAINEE;
    const isMembersAdded =
      item.type === NotificationType.MEMBERS_ADDED ||
      item.type === "members_added";
    const memberId = extractMemberIdFromNotificationData(item.data);

    if (
      isTrainee &&
      isMembersAdded &&
      memberId &&
      userDetailsFromCookie?.email_id
    ) {
      onClose();
      const session = await startTraineeChatWithMember({
        emailId: userDetailsFromCookie.email_id,
        memberId,
        currentPath: currentUrl,
        router,
      });
      if (session) {
        await hook.markRead(item.id);
        await hook.refresh();
      }
      return;
    }

    await hook.markRead(item.id);
    onClose();
    if (isTrainee) {
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("data-refresh-requested", {
            detail: {
              notification: {
                type: item.type,
                title: item.title,
                message: item.message,
                data: item.data,
              },
            },
          }),
        );
      }
      return;
    }
    router.push(item.redirect_url ?? "/dashboard");
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    document.body.classList.add("notification-drawer-open");
    return () => {
      document.body.classList.remove("notification-drawer-open");
    };
  }, [open]);

  if (!open || !mounted) return null;

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[100] max-md:hidden bg-gradient-to-l from-color-gray-dark/50 via-color-gray-dark/30 to-color-gray-dark/10 backdrop-blur-[3px]"
        onClick={onClose}
        aria-hidden="true"
      />

      <aside
        className="notification-drawer fixed z-[110] flex flex-col bg-color-white max-md:inset-0 max-md:h-dvh max-md:max-h-dvh max-md:w-full max-md:border-0 max-md:shadow-none md:right-0 md:top-0 md:h-full md:w-[min(24rem,92vw)] md:border-l md:border-color-gray-medium md:shadow-[0_8px_32px_rgba(0,0,0,0.1)]"
        aria-label="Notifications panel"
      >
        <header className="notification-drawer__header flex items-center justify-between gap-3 border-b border-color-gray-light bg-color-bg-blue px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-color-blue-light bg-color-white text-color-primary shadow-sm">
              <Bell size={20} strokeWidth={1.75} />
            </span>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-color-font-dark leading-tight">
                Notifications
              </h2>
              <p className="text-xs text-color-gray mt-0.5">
                {hook.unreadCount > 0
                  ? `${hook.unreadCount} unread`
                  : "You are all caught up"}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {dismissibleCount > 0 ? (
              <button
                type="button"
                onClick={handleClearAll}
                disabled={isClearing}
                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-color-primary hover:bg-color-white/80 disabled:opacity-50 transition-colors"
                aria-label="Clear all notifications except high priority"
                title="Clears all except high-priority notifications"
              >
                {isClearing ? "Clearing…" : "Clear all"}
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-lg text-color-gray hover:bg-color-white/80 hover:text-color-font-dark transition-colors"
              aria-label="Close notifications"
            >
              <X size={18} strokeWidth={1.75} />
            </button>
          </div>
        </header>

        <div className="notification-drawer__body flex-1 overflow-y-auto bg-color-gray-light/60 p-2 space-y-1.5">
          {hook.notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-color-gray-medium bg-color-white text-color-primary shadow-sm">
                <Bell size={24} strokeWidth={1.5} />
              </span>
              <div>
                <p className="text-sm font-medium text-color-font-dark">
                  No notifications yet
                </p>
                <p className="text-xs text-color-gray mt-1 max-w-[14rem]">
                  Updates about sessions, analysis, and account activity will
                  appear here. Use Clear all to dismiss non-urgent items; high
                  priority alerts stay until you open them.
                </p>
              </div>
            </div>
          ) : (
            hook.notifications.map((notification) => (
              <NotificationItem
                key={notification.id}
                item={notification}
                onClick={() => handleItemClick(notification)}
                onDiscard={() => hook.discard(notification.id)}
              />
            ))
          )}

          {hook.hasMore && (
            <button
              type="button"
              onClick={hook.loadMore}
              className="w-full rounded-lg py-3.5 text-sm font-medium text-color-primary hover:bg-color-white/70 transition-colors"
            >
              Load more
            </button>
          )}
        </div>
      </aside>
    </>,
    document.body,
  );
}
